import { IHttpApi } from '@cian/http-api';
import { useCallback, useEffect, useState } from 'react';
import { IGetRealEstateListV2ResponseSchema } from 'shared/repositories/cian-dobro/entities/property/GetRealEstateListV2ResponseSchema';
import { fetchChangeStatus, TChangeStatusResponse } from 'shared/repositories/cian-dobro/v1/change-status';
import {
  fetchGetRealEstateList,
  TGetRealEstateListResponse,
} from 'shared/repositories/cian-dobro/v2/get-real-estate-list';
import { EStatus } from 'shared/types/listing';
import { useApplicationContext } from 'shared/utils/applicationContext';

export type TLoadDataResult = IGetRealEstateListV2ResponseSchema | 'error' | 'no_access';

async function loadData(httpApi: IHttpApi): Promise<TLoadDataResult> {
  try {
    const res: TGetRealEstateListResponse = await fetchGetRealEstateList({ httpApi });

    if (res.statusCode === 400) {
      return 'no_access';
    }

    return res.response;
  } catch (err) {
    // oxlint-disable-next-line no-console
    console.error(err);

    return 'error';
  }
}

async function updateData(httpApi: IHttpApi, upd: IUpdateOffer) {
  try {
    const res: TChangeStatusResponse = await fetchChangeStatus({
      httpApi,
      parameters: upd,
    });

    if (res.statusCode === 400) {
      throw new Error('no access');
    }
  } catch (err) {
    // oxlint-disable-next-line no-console
    console.error(err);

    throw err;
  }
}

export interface IUpdateOffer {
  id: number;
  status: EStatus;
  comment: string;
}

export function useFetchDobroListing() {
  const { httpApi } = useApplicationContext();
  const [result, setResult] = useState<TLoadDataResult>();
  const [loading, setLoading] = useState(true);

  const loadOffers = useCallback(() => {
    loadData(httpApi)
      .then(setResult)
      .finally(() => setLoading(false));
  }, [httpApi]);

  useEffect(() => {
    loadOffers();
  }, []); // oxlint-disable-line react-hooks/exhaustive-deps

  const updateOffer = useCallback(
    async (upd: IUpdateOffer) => {
      /* istanbul ignore if */
      if (!result || result === 'error' || result === 'no_access') {
        return;
      }

      const offer = result.offers.find(o => o.id === upd.id);
      /* istanbul ignore if */
      if (!offer) {
        return;
      }

      await updateData(httpApi, upd);

      offer.status = upd.status;
      offer.comment = upd.comment;

      setResult({ offers: result.offers, userMeta: result.userMeta });
    },
    [result, setResult, httpApi],
  );

  return { result, loading, loadOffers, updateOffer };
}
